import type { FieldProvenanceDto } from '@@types/api.types';

/**
 * Composite provenance-map keys for multi-value MA sections (ADR-0007 / AZ-449).
 *
 * Per-item keys mark which individual items in a collection came from AI, keyed
 * by the item's natural id. The write side (Full Generation accept) and the read
 * side (MA details) both build keys here so they always match. Interoperability /
 * Interchangeable platform alignments and Task Recommendations have no stable
 * item id (their identity is editable free text), so they stay section-level.
 */
export const provenanceItemKeys = {
  capability: (id: string) => `CAPABILITIES:${id}`,
  community: (id: string) => `COMMUNITIES:${id}`,
  effect: (effectId: string, useId: string, domainId: string) => `EFFECTS:${effectId}:${useId}:${domainId}`,
  killChain: (id: string) => `KILL_CHAINS:${id}`,
  service: (serviceId: string) => `RECOMMENDED_SERVICES:${serviceId}`,
  technologyUse: (useId: string) => `RECOMMENDED_TECHNOLOGY_USES:${useId}`,
} as const;

/**
 * One field's provenance signal raised when an AI Suggestion / section is
 * accepted. Its key's presence marks the field/item AI_ASSISTED; `llmLogId`
 * references the generating log.
 */
export type ProvenanceAcceptEntry = {
  key: string;
  llmLogId?: string;
};

/**
 * Roll up a section's per-item provenance into a single badge for collections
 * rendered as chips/avatars (Capabilities, Communities, Kill Chains, Services),
 * where a per-item sparkle would be too noisy. Returns the first AI-assisted
 * entry in the section, or null. Card-based collections (Effects, Tech Uses)
 * badge per item instead and don't use this.
 */
export const sectionProvenance = (
  provenance: Record<string, FieldProvenanceDto> | null | undefined,
  sectionPrefix: string,
): FieldProvenanceDto | null => {
  if (!provenance) {
    return null;
  }

  const prefix = `${sectionPrefix}:`;
  for (const [key, value] of Object.entries(provenance)) {
    if (key === sectionPrefix || key.startsWith(prefix)) {
      return value;
    }
  }

  return null;
};
